'use client'

import React, { type FormEvent, useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import authSelector from '@/redux/selectors/auth'
import { UserRole } from '@/types/firebase'
import { authRedirectPathSwitchObj } from '@/utils/auth'
import { AxiAvatar } from '@/components/axi/AxiAvatar'
import { PlainTextMath } from '@/components/axi/PlainTextMath'
import {
  decodeCarriedHistory,
  encodeCarriedHistory,
  newCarriedNonce,
  safeCarriedQuestionImage,
  type ICarriedChatTurn,
} from '@/utils/carried-chat'

import { trackEventMixpanel } from '@/utils/mixpanel'
import { AXI_MESSAGE_SENT, SIGN_UP_WALL_SHOWN } from '@/utils/mixpanel/eventNames'
import { axiMessageSentProps } from '@/utils/mixpanel/events/features'

type Turn = ICarriedChatTurn

export default function AskAxiPage() {
  const { user } = useSelector(authSelector)
  const [turns, setTurns] = useState<Turn[]>([])
  const [message, setMessage] = useState('')
  const [remaining, setRemaining] = useState<number | null>(null)
  const [sending, setSending] = useState(false)
  const [wall, setWall] = useState(false)
  const [error, setError] = useState('')
  const [ready, setReady] = useState(false)
  // One handoff id per visit: the same question asked again on a later visit
  // starts its own conversation; a reload of this visit keeps its nonce.
  const [nonce, setNonce] = useState<string>()
  // A chat carried from a website question asks about that question; a
  // homepage visit starts empty and gets a general prompt.
  const [isCarried, setIsCarried] = useState(false)

  useEffect(() => {
    const carried = decodeCarriedHistory()
    if (carried?.length) {
      setTurns(carried)
      setIsCarried(true)
    }
    setNonce(carried?.nonce ?? newCarriedNonce())
    setReady(true)
  }, [])

  useEffect(() => {
    if (!ready || !user) return
    // Only students can own the carried student conversation. Other roles
    // return home without entering the student route or seeding the handoff.
    if (user.role !== UserRole.Student) {
      window.location.replace(authRedirectPathSwitchObj[user.role] || '/')
      return
    }
    const params = new URLSearchParams(window.location.search)
    params.set('chat', '1')
    window.location.replace(
      `/student/ai-assistant?${params.toString()}${encodeCarriedHistory(
        turns,
        nonce,
      )}`,
    )
  }, [ready, user, turns, nonce])

  async function ask(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    const question = message.trim()
    if (!question || sending || wall || user) return

    setSending(true)
    setError('')
    try {
      const params = new URLSearchParams(window.location.search)
      const response = await fetch('/api/axi-demo-chat', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          message: question,
          history: turns
            .slice(-6)
            .map(({ role, content }) => ({ role, content })),
          pageContext: {
            lesson: params.get('lesson') || undefined,
            topic: params.get('topic') || undefined,
          },
        }),
      })
      const result = await response.json()
      if (response.status === 429 && result.code === 'signup_required') {
        setTurns([...turns, { role: 'user', content: question }])
        setMessage('')
        setRemaining(0)
        setWall(true)
        trackEventMixpanel(SIGN_UP_WALL_SHOWN, {
          surface: 'ask_axi',
          reason: 'daily_quota',
        })
        return
      }
      if (!response.ok || typeof result.answer !== 'string') {
        setError(
          typeof result.error === 'string'
            ? result.error
            : 'Axi could not answer. Please try again.',
        )
        return
      }
      const updated: Turn[] = [
        ...turns,
        { role: 'user', content: question },
        { role: 'assistant', content: result.answer },
      ]
      setTurns(updated)
      setMessage('')
      trackEventMixpanel(
        AXI_MESSAGE_SENT,
        axiMessageSentProps({
          surface: 'ask_axi_guest',
          priorUserMessages: turns.filter((t) => t.role === 'user').length,
          hasImage: false,
          isGuest: true,
        }),
      )
      if (typeof result.remaining === 'number') {
        setRemaining(result.remaining)
        if (result.remaining <= 0) {
          setWall(true)
          trackEventMixpanel(SIGN_UP_WALL_SHOWN, {
            surface: 'ask_axi',
            reason: 'daily_quota',
          })
        }
      }
    } catch {
      setError('Axi could not connect. Please try again.')
    } finally {
      setSending(false)
    }
  }

  if (!ready || user) return null

  const params = new URLSearchParams(window.location.search)
  params.set('chat', '1')
  const suffix = `?${params.toString()}${encodeCarriedHistory(turns, nonce)}`

  return (
    <main className="min-h-screen bg-[#f7f8fc] px-4 py-10 text-[#182143]">
      <div className="mx-auto max-w-2xl rounded-2xl border border-[#e4e8f2] bg-white p-5 shadow-sm sm:p-8">
        <header className="mb-6 flex items-center gap-4">
          <AxiAvatar size={56} />
          <div>
            <h1 className="text-2xl font-bold">Ask Axi</h1>
            <p className="text-sm text-slate-600">
              Work through a maths question, one step at a time.
            </p>
          </div>
        </header>
        <p className="mb-5 text-sm text-slate-600" aria-live="polite">
          {remaining === null
            ? 'You can ask up to 3 free questions a day.'
            : `${remaining} free ${
                remaining === 1 ? 'question' : 'questions'
              } left today.`}
        </p>
        <div className="mb-5 space-y-4" aria-live="polite">
          {turns.map((turn, index) => (
            <div
              key={index}
              className={
                turn.role === 'user'
                  ? 'ml-8 rounded-xl bg-[#eaf0ff] p-4'
                  : 'mr-8 rounded-xl bg-[#f4f6fa] p-4'
              }
            >
              <span className="mb-2 block text-xs font-semibold">
                {turn.role === 'assistant' ? 'Axi' : 'You'}
              </span>
              {turn.role === 'user' &&
                safeCarriedQuestionImage(turn.imageUrl) && (
                  <img
                    src={safeCarriedQuestionImage(turn.imageUrl)}
                    alt="Your carried exam question"
                    className="mb-3 max-w-full rounded-lg border"
                  />
                )}
              <PlainTextMath
                text={turn.content.replace(/<br\s*\/?>/gi, '\n')}
              />
            </div>
          ))}
        </div>
        {wall ? (
          <div
            className="rounded-xl border border-[#d5defe] bg-[#f2f5ff] p-5"
            role="status"
          >
            <h2 className="font-semibold">Keep going with Axi</h2>
            <p className="my-2 text-sm">
              You've used your 3 free questions today. Create a free account to
              continue this conversation.
            </p>
            <div className="flex flex-wrap gap-3">
              <a
                className="rounded-lg bg-[#3157ff] px-4 py-2 font-semibold text-white"
                href={`/register${suffix}`}
              >
                Create free account
              </a>
              <a
                className="rounded-lg border border-[#3157ff] px-4 py-2 font-semibold text-[#3157ff]"
                href={`/login${suffix}`}
              >
                Sign in
              </a>
            </div>
          </div>
        ) : (
          <form onSubmit={ask}>
            <label
              htmlFor="axi-guest-question"
              className="mb-2 block font-medium"
            >
              Your question
            </label>
            <textarea
              id="axi-guest-question"
              value={message}
              onChange={(event) => setMessage(event.target.value)}
              maxLength={1000}
              rows={3}
              className="w-full rounded-lg border border-[#cbd3e3] p-3"
              placeholder={
                isCarried
                  ? 'What part of the question are you stuck on?'
                  : 'What would you like help with?'
              }
              disabled={sending}
            />
            {error && (
              <p role="alert" className="my-2 text-sm text-red-700">
                {error}
              </p>
            )}
            <button
              type="submit"
              disabled={sending || !message.trim()}
              className="mt-3 rounded-lg bg-[#3157ff] px-5 py-2 font-semibold text-white disabled:opacity-50"
            >
              {sending ? 'Axi is thinking...' : 'Ask Axi'}
            </button>
          </form>
        )}
      </div>
    </main>
  )
}
